import { useCallback, useState } from 'react';
import type { RefCallback } from 'react';

/**
 * @see [React docs for suggested pattern](https://react.dev/learn/manipulating-the-dom-with-refs#how-to-manage-a-list-of-refs-using-a-ref-callback)
 * @returns A tuple containing a map of refs and a function to get a ref callback for a given id.
 */
const useRefMap = <T>() => {
  const [map] = useState(() => new Map<string, T>());
  const [cbMap] = useState(() => new Map<string, RefCallback<T>>());

  const getCallbackRef = useCallback((id: string): RefCallback<T> => {
    if (cbMap.has(id)) {
      return cbMap.get(id)!;
    }

    const cb: RefCallback<T> = el => {
      if (el !== null) {
        map.set(id, el);
      } else {
        map.delete(id);
      }
    };

    cbMap.set(id, cb);

    return cb;
  }, []);

  return [map, getCallbackRef] satisfies [Map<string, T>, (id: string) => RefCallback<T>];
};

export default useRefMap;
